/* One Shopify mutation at a time. Render authoritative Liquid sections; never guess totals. */
(() => {
  if (customElements.get('zb-cart-page')) return;
  class ZivaBuyCartPage extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.setAttribute('data-ready', '');
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('submit', event => this.onSubmit(event), options);
      this.disabledState = new Map();
      this.busy = false;
      this.stale = false;
    }
    disconnectedCallback() { this.controller?.abort(); }
    rootURL(path) { return `${this.dataset.root.replace(/\/?$/, '/')}${path}`; }
    clearError() {
      this.querySelector('[data-zb-cart-error]').hidden = true;
      this.querySelector('[data-zb-cart-error-text]').textContent = '';
    }
    showError(message) {
      const error = this.querySelector('[data-zb-cart-error]');
      error.querySelector('[data-zb-cart-error-text]').textContent = message;
      error.hidden = false;
      error.focus({ preventScroll: true });
    }
    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.disabledState.clear();
        this.querySelectorAll('button,input,textarea').forEach(control => {
          this.disabledState.set(control, control.disabled); control.disabled = true;
        });
      } else {
        this.disabledState.forEach((disabled, control) => { if (control.isConnected) control.disabled = disabled; });
        this.disabledState.clear();
      }
    }
    validateQuantity(input, quantity) {
      const minimum = Number(input.dataset.min) || 1;
      const increment = Number(input.dataset.step) || 1;
      const maximum = input.hasAttribute('max') ? Number(input.max) : Infinity;
      if (!Number.isSafeInteger(quantity) || quantity < 0) return 'Please enter a whole-number quantity of zero or more.';
      if (quantity > 0 && (quantity < minimum || (quantity - minimum) % increment !== 0)) return `Choose at least ${minimum}, in increments of ${increment}.`;
      if (quantity > maximum) return `The maximum quantity for this item is ${maximum}.`;
      return '';
    }
    changeLine(input, quantity, focusID) {
      const issue = this.validateQuantity(input, quantity);
      if (issue) { input.value = input.dataset.current; this.showError(issue); return Promise.resolve(); }
      const line = input.closest('[data-zb-cart-line]');
      return this.mutate('cart/change.js', { id: line.dataset.key, quantity }, quantity === 0 ? `${line.dataset.title} removed from your bag.` : `Quantity updated for ${line.dataset.title}.`, { focusID });
    }
    onClick(event) {
      const step = event.target.closest('[data-zb-cart-step]');
      const remove = event.target.closest('[data-zb-cart-remove]');
      const note = event.target.closest('[data-zb-cart-note-save]');
      const clear = event.target.closest('[data-zb-cart-clear-codes]');
      if (!step && !remove && !note && !clear) return;
      event.preventDefault();
      if (this.busy || this.stale) return;
      if (step || remove) {
        const line = (step || remove).closest('[data-zb-cart-line]');
        const input = line.querySelector('[data-zb-cart-quantity]');
        const next = remove ? 0 : Number(input.value) + Number(step.dataset.zbCartStep) * (Number(input.dataset.step) || 1);
        this.changeLine(input, next, remove ? `ZbCartQty-${line.dataset.index}` : step.id);
      } else if (note) {
        this.mutate('cart/update.js', { note: this.querySelector('#ZbCartNote').value }, 'Order note saved.', { focusID: 'ZbCartNote' });
      } else if (clear) {
        this.mutate('cart/update.js', { discount: '' }, 'Discount codes removed. Automatic discounts, if eligible, remain.', { focusID: 'ZbCartDiscount' });
      }
    }
    onChange(event) {
      if (!event.target.matches('[data-zb-cart-quantity]') || this.busy || this.stale) return;
      this.changeLine(event.target, Number(event.target.value || NaN), event.target.id);
    }
    async onSubmit(event) {
      const form = event.target;
      if (form.matches('[data-zb-cart-add-form]')) {
        event.preventDefault();
        if (this.busy || this.stale) return;
        const id = form.elements.id.value;
        this.mutate('cart/add.js', { items: [{ id, quantity: 1 }] }, `${event.submitter?.dataset.title || 'Product'} added to your bag.`);
      } else if (form.matches('[data-zb-cart-discount-form]')) {
        event.preventDefault();
        if (this.busy || this.stale) return;
        const entered = form.elements.discount.value.trim();
        if (!entered) return;
        const existing = [...this.querySelectorAll('[data-zb-cart-code]')].map(node => node.dataset.zbCartCode);
        const codes = [...new Set([...existing, ...entered.split(',').map(code => code.trim()).filter(Boolean)])];
        this.mutate('cart/update.js', { discount: codes.join(',') }, 'Discount code checked.', { focusID: 'ZbCartDiscount', discountCodes: entered.split(',').map(code => code.trim()).filter(Boolean) });
      } else if (form.matches('[data-zb-cart-form]')) {
        if (this.busy || this.stale) { event.preventDefault(); return; }
        if (event.submitter?.name === 'checkout') return; // Shopify owns checkout and final inventory validation.
        event.preventDefault();
        const values = [...form.querySelectorAll('[data-zb-cart-quantity]')].map(input => ({ input, quantity: Number(input.value || NaN), key: input.closest('[data-zb-cart-line]').dataset.key }));
        for (const value of values) {
          const issue = this.validateQuantity(value.input, value.quantity);
          if (issue) { this.showError(issue); return; }
        }
        // change.js validates inventory, unlike an updates payload to update.js.
        for (const value of values) {
          if (value.quantity === Number(value.input.dataset.current)) continue;
          const succeeded = await this.mutate('cart/change.js', { id: value.key, quantity: value.quantity }, 'Your bag was updated.');
          if (!succeeded) return;
        }
        const note = form.querySelector('[name="note"]');
        if (note) await this.mutate('cart/update.js', { note: note.value }, 'Your bag and order note were saved.');
        else this.querySelector('[data-zb-cart-status]').textContent = 'Your bag is up to date.';
      }
    }
    async mutate(path, payload, message, options = {}) {
      if (this.busy || this.stale) return false;
      const draftNote = this.querySelector('#ZbCartNote')?.value;
      const draftCode = this.querySelector('#ZbCartDiscount')?.value;
      const openDetails = [...this.querySelectorAll('details[open]')].map(node => node.className);
      const focusID = options.focusID || document.activeElement?.id;
      this.clearError(); this.setBusy(true);
      let confirmed = false;
      let uncertain = true;
      try {
        const response = await fetch(this.rootURL(path), {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...payload, sections: [this.dataset.section, 'cart-icon-bubble'], sections_url: this.dataset.cartUrl }),
        }).catch(() => { throw new Error('We could not confirm this change. Please refresh your bag before trying again.'); });
        let data;
        try { data = await response.json(); } catch { throw new Error('We could not confirm this change. Please refresh your bag before trying again.'); }
        if (!response.ok || data.status >= 400) {
          uncertain = false;
          throw new Error(data.description || data.message || 'Your bag could not be updated. Please try again.');
        }
        confirmed = true; uncertain = false;
        const html = data.sections?.[this.dataset.section];
        if (typeof html !== 'string') throw new Error('Your change was saved, but the bag could not refresh. Please refresh your bag to see the latest total.');
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const incoming = documentFragment.querySelector('zb-cart-page [data-zb-cart-content]');
        if (!incoming) throw new Error('Your change was saved, but the bag could not refresh. Please refresh your bag to see the latest total.');
        this.querySelector('[data-zb-cart-content]').replaceChildren(...incoming.childNodes);
        const note = this.querySelector('#ZbCartNote');
        if (note && draftNote !== undefined) note.value = draftNote;
        const code = this.querySelector('#ZbCartDiscount');
        if (code && draftCode !== undefined) code.value = payload.discount === '' ? '' : draftCode;
        this.querySelectorAll('details').forEach(node => { if (openDetails.includes(node.className)) node.open = true; });
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && data.sections?.['cart-icon-bubble']) {
          const bubbleHTML = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
          if (bubbleHTML) bubble.replaceChildren(...bubbleHTML.childNodes);
        }
        // The section is authoritative; add.js responses do not contain a full cart count.
        const itemCount = Number(this.querySelector('[data-zb-cart-item-count]')?.dataset.zbCartItemCount || 0);
        document.querySelectorAll('[data-cart-count],[data-zb-cart-count]').forEach(node => { node.textContent = String(itemCount); node.hidden = itemCount === 0; node.style.display = itemCount ? '' : 'none'; });
        if (options.discountCodes) {
          const applied = [...this.querySelectorAll('[data-zb-cart-code]')].map(node => node.dataset.zbCartCode.toLowerCase());
          const matched = options.discountCodes.every(codeValue => applied.includes(codeValue.toLowerCase()));
          message = matched ? 'Discount code applied. Your total is up to date.' : 'This code did not discount the current bag. Check the code and eligibility, or verify it at checkout.';
        }
        this.querySelector('[data-zb-cart-status]').textContent = message;
        if (options.discountCodes && !message.startsWith('Discount code applied')) this.showError(message);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && data.items && typeof data.item_count === 'number') {
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zivabuy-cart-page', cartData: data })).catch(() => {});
        }
        document.dispatchEvent(new CustomEvent('zivabuy:cart-updated', { detail: { item_count: itemCount } }));
        this.setBusy(false);
        if (!options.discountCodes || message.startsWith('Discount code applied')) {
          const focusTarget = (focusID ? document.getElementById(focusID) : null) || this.querySelector('#ZbCartHeading');
          if (focusTarget && this.contains(focusTarget)) focusTarget.focus({ preventScroll: true });
        }
        return true;
      } catch (error) {
        this.setBusy(false);
        if (confirmed || uncertain) {
          this.stale = true;
          this.querySelectorAll('button,input,textarea').forEach(control => { control.disabled = true; });
        } else {
          this.querySelectorAll('[data-zb-cart-quantity]').forEach(input => { input.value = input.dataset.current; });
        }
        this.showError(error.message || 'Your bag could not be updated. Please try again.');
        return false;
      } finally { if (this.busy) this.setBusy(false); }
    }
  }
  customElements.define('zb-cart-page', ZivaBuyCartPage);
})();
